import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react';

/**
 * Tooltip / in-context explanation (card Cibmk2YuSlFB — JitsLab parity).
 *
 * An in-context explanation beside a control: tap to reveal on touch (no
 * hover-only), dismissible, never blocks the control it describes, and never
 * takes over the control's 44px tap floor — it sits beside it as its own
 * target. Rendered in-place (position relative to the trigger) so it cannot
 * overlap fixed chrome or the tab bar.
 *
 * Wrap a BARE trigger (a `<span className="pear-tip-trigger">ⓘ</span>`), not
 * the control itself: the control keeps its own semantics and its own tap
 * target, and this stays one extra tab stop rather than a nested button.
 *
 * Usage:
 *   <Tooltip label="Turning this off stops the agent from offering tags.">
 *     <Switch ... />
 *   </Tooltip>
 */
/**
 * The window the bubble actually has to live in: the nearest ancestor that
 * clips vertically (a panel's `overflow-y: auto` is the common one on these
 * screens), or the viewport when nothing clips. Card sZwixtT5BCac — the flip
 * decision needs a real ceiling, and the sticky tab row that swallowed the
 * bubble sits inside the scrollport, not above it.
 */
function scrollPort(el: HTMLElement): { top: number; bottom: number } {
  let p = el.parentElement;
  while (p && p !== document.body) {
    const cs = getComputedStyle(p);
    if (cs.overflowY !== 'visible') {
      const r = p.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom };
    }
    p = p.parentElement;
  }
  return { top: 0, bottom: window.innerHeight };
}

export function Tooltip({ label, children, tipFor, id }: { label: string; children?: ReactNode; tipFor?: string; id?: string }) {
  const glyph = children === undefined;
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  /* The bubble is centred on its trigger, so a trigger near either edge used
     to hang the bubble off the viewport — clipped on a phone (founder email
     2026-10-25, card o4i7UUbHTn5M). Measure it after it paints and shift it
     back inside; the CSS caps the width, this fixes the position. */
  const bubble = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const el = bubble.current;
    if (!el) return;
    /* Start from the authored placement every time: the flip below is a
       measured per-open decision, never carried over from the last open. */
    el.style.marginLeft = '0px';
    el.removeAttribute('data-placement');
    const host = wrap.current;
    const port = host ? scrollPort(host) : { top: 0, bottom: window.innerHeight };
    const r = el.getBoundingClientRect();
    /* Card sZwixtT5BCac — the bubble opens ABOVE its trigger, and the only
       clamp was horizontal. On a surface whose first heading sits under the
       sticky sub-tab row there is no room up there: the opaque row and the
       panel's own overflow swallowed the whole bubble (Jonathan, 2026-11-03:
       "the tooltip is swallowed by the tabs at top (this is true across
       life)"). If the space above the trigger cannot hold the bubble and
       there is more room below, open downward instead — a measured flip, so
       every existing caller with room above keeps its authored placement. */
    if (host) {
      const h = host.getBoundingClientRect();
      const pad = 8;
      const above = h.top - port.top - pad;
      const below = port.bottom - h.bottom - pad;
      if (r.height > above && below > above) el.dataset.placement = 'below';
    }
    const pad = 8;
    let dx = 0;
    if (r.left < pad) dx = pad - r.left;
    else if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right;
    if (dx !== 0) el.style.marginLeft = `${Math.round(dx)}px`;
  }, [open]);

  // Close on an outside tap and on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    /* Escape belongs to the tooltip, not to whatever overlay or composer
       listens further up: without this the capture bar under it took the same
       Escape as "clear the draft" and closed itself (mG5eA32qztRg). */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      e.stopPropagation();
    };
    /* CAPTURE phase, deliberately: React's synthetic keydown runs on the
       container's bubble path, so a bubble-phase listener here would only fire
       AFTER the composer had already taken the key (the capture bar cleared
       itself and closed on an Escape meant for the tip). Capturing at the
       document stops the event before it reaches React at all. */
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      id={id}
      className={glyph ? 'pear-tooltip pear-tip-trigger' : 'pear-tooltip'}
      {...(glyph && tipFor ? { 'data-tip-for': tipFor } : {})}
      // Focusable and named: an explanation a keyboard or screen-reader user
      // cannot reach is not an explanation (card Cibmk2YuSlFB). Tooltip wraps
      // a BARE trigger — never an interactive control, which keeps the role
      // honest and stops the tap target from doubling up.
      tabIndex={0}
      role="button"
      aria-label={label}
      aria-expanded={open}
      onClick={() => setOpen(o => !o)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(o => !o)}
    >
      {/* Card f1w1KsNRc3kx — the ⓘ shorthand form. The class, the role, the
          name and the tab stop all live on the ONE element a person (or a
          screen reader) can actually reach; before, the named wrapper was the
          tooltip while the `.pear-tip-trigger` inside it was a bare glyph with
          a `title` that only a mouse ever saw. The glyph itself is decorative
          by construction, so nothing reads "ⓘ" as a name. */}
      {glyph ? <span aria-hidden="true">ⓘ</span> : children}
      {open && (
        <span className="pear-tooltip-bubble" role="tooltip" ref={bubble}>
          {label}
        </span>
      )}
    </span>
  );
}
